import { getLinkComment, getLinkDisabled, getPortalLabel, isPortalLinkEntry, normalizeLinkStatus, splitAdvertiseLinks } from '../../../../functions/agent/advertises/portalLinks'
import { MANUAL_ADVERTISEMENT_PORTAL_OPTIONS } from './manualOperations.config'
import { type ManualAdvertisementFormValues, type ManualAdvertisementPayload, type ManualAdvertisementPortal, type ManualAdvertisementPortalLink } from './manualAdvertisement.types'

const toDubaiIsoDate = (dateLocal: string): string => new Date(`${dateLocal}T12:00:00+04:00`).toISOString()

const calculateDurationDays = (startDate: string, endDate: string): number => {
    const start = new Date(`${startDate}T12:00:00+04:00`).getTime()
    const end = new Date(`${endDate}T12:00:00+04:00`).getTime()
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 0
    return Math.max(1, Math.ceil((end - start) / (24 * 60 * 60 * 1000)))
}

const normalizeManualPortal = (portal: string): ManualAdvertisementPortal =>
    (MANUAL_ADVERTISEMENT_PORTAL_OPTIONS as readonly string[]).includes(portal) ? portal as ManualAdvertisementPortal : 'Other'

const buildAdvertisementLinks = (values: ManualAdvertisementFormValues): Array<Record<string, any>> => {
    const links: Array<Record<string, any>> = []
    const trakheesiId = values.trakheesiId.trim()
    if (trakheesiId !== '') {
        links.push({
            Trakheesi_id: trakheesiId,
            Title: 'Trakheesi',
            Status: 'Approved'
        })
    }

    values.portalLinks
        .map((portalLink) => {
            const portal = normalizeManualPortal(portalLink.portal.trim())
            const comment = portalLink.comment.trim()
            return {
                Portal: portal,
                Title: portal,
                Link: portalLink.link.trim(),
                Status: 'Approved',
                Comment: comment,
                Feedback: comment,
                IsDisabled: false,
                Disabled: false
            }
        })
        .filter((portalLink) => portalLink.Link !== '')
        .forEach((portalLink) => links.push(portalLink))

    return links
}

export const buildManualAdvertisementPayload = (values: ManualAdvertisementFormValues): ManualAdvertisementPayload => {
    const duration = calculateDurationDays(values.startDate, values.endDate)
    return {
        Name: `Advertisement - ${values.unit.propertyName} - ${values.unit.headline}`,
        Duration: duration,
        User: values.userId,
        Unit: values.unit.unitId,
        CreditsAmount: 0,
        Notify: 'Agent',
        Statuses: [values.status],
        StartDate: toDubaiIsoDate(values.startDate),
        EndDate: toDubaiIsoDate(values.endDate),
        Links: buildAdvertisementLinks(values)
    }
}

export const mapAdvertiseLinksToManualPortalLinks = (advertise: any): ManualAdvertisementPortalLink[] =>
    splitAdvertiseLinks(advertise?.attributes?.Links)
        .portals
        .map((link) => ({
            id: `${String(link.__sourceIndex)}-${String(link.Link ?? '')}`,
            sourceIndex: link.__sourceIndex,
            portal: normalizeManualPortal(getPortalLabel(link)),
            link: typeof link.Link === 'string' ? link.Link : '',
            comment: getLinkComment(link),
            disabled: getLinkDisabled(link),
            status: normalizeLinkStatus(link.Status)
        }))

const portalKey = (value: string): string => value.trim().toLowerCase()

export const buildManualAdvertisementLinksUpdate = (advertise: any, portalLinks: ManualAdvertisementPortalLink[], trakheesiId: string): Array<Record<string, any>> => {
    const splitLinks = splitAdvertiseLinks(advertise?.attributes?.Links)
    const updatesBySourceIndex = new Map<number, ManualAdvertisementPortalLink>()
    const newPortalLinks: ManualAdvertisementPortalLink[] = []

    portalLinks.forEach((portalLink) => {
        if (typeof portalLink.sourceIndex === 'number') {
            updatesBySourceIndex.set(portalLink.sourceIndex, portalLink)
            return
        }
        if (portalLink.portal.trim() !== '' || portalLink.link.trim() !== '') {
            newPortalLinks.push(portalLink)
        }
    })

    const replacingPortalNames = new Set(newPortalLinks.map((portalLink) => portalKey(portalLink.portal)).filter((key) => key !== ''))

    let trakheesiUpdated = false
    const nextTrakheesiId = trakheesiId.trim()

    const keptLinks = splitLinks.all.map(({ __sourceIndex, ...link }) => {
        if (!isPortalLinkEntry(link)) {
            if (nextTrakheesiId === '') return link
            trakheesiUpdated = true
            return {
                ...link,
                Title: 'Trakheesi',
                Status: normalizeLinkStatus(link.Status),
                Trakheesi_id: nextTrakheesiId
            }
        }

        const existingPortalKey = portalKey(getPortalLabel(link))
        const update = updatesBySourceIndex.get(__sourceIndex)
        const { Trakheesi_id, ...portalLink } = link

        if (update != null) {
            return {
                ...portalLink,
                Portal: normalizeManualPortal(update.portal.trim()),
                Title: normalizeManualPortal(update.portal.trim()),
                Link: update.link.trim(),
                Comment: update.comment.trim(),
                Feedback: update.comment.trim(),
                Status: 'Approved',
                Disabled: update.disabled,
                IsDisabled: update.disabled
            }
        }

        if (existingPortalKey !== '' && replacingPortalNames.has(existingPortalKey)) {
            return {
                ...portalLink,
                Disabled: true,
                IsDisabled: true
            }
        }

        return portalLink
    })

    const existingNewKeys = new Set<string>()
    const trakheesiLinks = !trakheesiUpdated && nextTrakheesiId !== ''
        ? [{
            Title: 'Trakheesi',
            Status: 'Approved',
            Trakheesi_id: nextTrakheesiId
        }]
        : []
    const addedLinks = newPortalLinks
        .map((portalLink) => {
            const portal = normalizeManualPortal(portalLink.portal.trim())
            const comment = portalLink.comment.trim()
            return {
                Portal: portal,
                Title: portal,
                Link: portalLink.link.trim(),
                Status: 'Approved',
                Comment: comment,
                Feedback: comment,
                Disabled: false,
                IsDisabled: false
            }
        })
        .filter((portalLink) => portalLink.Link !== '')
        .filter((portalLink) => {
            const key = `${portalKey(portalLink.Portal)}::${portalLink.Link.trim().toLowerCase()}`
            if (existingNewKeys.has(key)) return false
            existingNewKeys.add(key)
            return true
        })

    return [...keptLinks, ...trakheesiLinks, ...addedLinks]
}
